<template>
  <div
    class="am3-icon relative"
    :style="iconStyle"
  >
    <!-- Use key to force re-render when type or color changes -->
    <component
      :is="iconComponent"
      :key="type+'_'+color"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, defineAsyncComponent } from 'vue'
import type { PropType } from 'vue'
import '@/components/am3-generic/core/theme.css'
import { iconList } from './svgs/_iconList'

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full'
export type IconColor = 'default' | 'informative' | 'brand' | 'danger' | 'warning' | 'success' | 'white' | 'strong' | 'weak' | 'currentColor'

/**
 * AM3Icon component
 *
 * A flexible icon component that dynamically loads SVG icons
 */
export default defineComponent({
  name: 'AM3Icon',

  props: {
    // The icon type (corresponds to SVG filename)
    type: {
      type: String,
      required: true,
      validator: (value: string) => iconList.includes(value),
    },

    // Icon size (s: 16px, m: 20px, l: 24px, xl: 32px)
    size: {
      type: String as PropType<IconSize>,
      default: 'md',
      validator: (value: string) => ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', 'full'].includes(value),
    },

    // Icon color (semantic color name or direct color value)
    color: {
      type: String as PropType<IconColor | string>,
      default: 'currentColor',
      validator: (value: string) => [
        'default', 'informative', 'brand', 'danger', 'secondary',
        'warning', 'success', 'white', 'strong', 'weak',
      ].includes(value) || value.startsWith('#') || value.startsWith('var(--') || value === 'currentColor',
    },
  },

  setup(props) {
    // Size mapping in pixels
    const sizeMap = {
      'xs': '12px',
      'sm': '16px',
      'md': '20px',
      'lg': '24px',
      'xl': '32px',
      '2xl': '48px',
      '3xl': '64px',
      'full': '100%', // useful for illustrations
    }

    // Create a reactive key based on type prop
    const typeKey = computed(() => props.type)

    // Dynamically load SVG component based on type
    const iconComponent = computed(() => {
      const type = props.type // This is a hack to bypass vue bug not watching the props if it's only used within a function
      return defineAsyncComponent({
        loader: () => import(`./svgs/${type}.svg`),
        onError: (error) => {
          console.error(`Failed to load icon: ${type}.svg`, error)
        },
      })
    })

    // Map semantic color names to CSS variables
    const getColorValue = (color: string) => {
      if (color.startsWith('#') || color.startsWith('var(--')) {
        return color
      }

      const colorMap: Record<string, string> = {
        default: 'var(--icons-default)',
        informative: 'var(--icons-informative)',
        brand: 'var(--icons-brand)',
        danger: 'var(--icons-danger)',
        warning: 'var(--icons-warning)',
        success: 'var(--icons-success)',
        white: 'var(--icons-white)',
        strong: 'var(--icons-strong)',
        weak: 'var(--icons-weak)',
        currentColor: 'currentColor',
      }

      return colorMap[color] || 'var(--icons-default)'
    }

    // Compute styles based on props - will reactively update when props change
    const iconStyle = computed(() => ({
      width: sizeMap[props.size],
      height: sizeMap[props.size],
      color: getColorValue(props.color),
    }))

    return {
      iconComponent,
      iconStyle,
    }
  },
})
</script>

<style scoped>
.am3-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
    overflow: visible;
  }
}
</style>
